import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Calendar as CalendarComponent } from '@codes/ui-kit/components/ui/calendar';
import { Card, CardContent } from '@codes/ui-kit/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@codes/ui-kit/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@codes/ui-kit/components/ui/select';
import { endOfMonth, format, startOfMonth, subDays, subMonths } from 'date-fns';
import { Calendar, CalendarDays, Filter, RefreshCw } from 'lucide-react';

interface FilterSectionProps {
    selectedCompany: string;
    setSelectedCompany: (value: string) => void;
    startDate: Date;
    setStartDate: (date: Date) => void;
    endDate: Date;
    setEndDate: (date: Date) => void;
    getMinStartDate: (companyId: string) => Date;
    getMaxEndDate: (companyId: string) => Date;
    handleRefresh: () => void;
    isFetching: boolean;
}

export const FilterSection: React.FC<FilterSectionProps> = ({
    selectedCompany,
    setSelectedCompany,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    getMinStartDate,
    getMaxEndDate,
    handleRefresh,
    isFetching,
}) => {
    const { t } = useTranslation();

    const selectCurrentMonth = () => {
        const now = new Date();
        const yesterday = subDays(now, 1);
        setStartDate(startOfMonth(now));
        const currentMonth = now.getMonth();
        const yesterdayMonth = yesterday.getMonth();
        if (currentMonth === yesterdayMonth) {
            setEndDate(yesterday);
        } else {
            setEndDate(endOfMonth(now));
        }
    };

    const selectLastMonth = () => {
        const lastMonth = subMonths(new Date(), 1);
        setStartDate(startOfMonth(lastMonth));
        setEndDate(endOfMonth(lastMonth));
    };

    const selectLastThreeMonths = () => {
        const now = new Date();
        const yesterday = subDays(now, 1);
        const threeMonthsAgo = subMonths(now, 3);
        setStartDate(startOfMonth(threeMonthsAgo));
        setEndDate(yesterday);
    };

    return (
        <Card>
            <CardContent className="p-6">
                <div className="flex flex-wrap gap-[26px]">
                    <div className="flex gap-4 flex-wrap flex-1">
                        <div className="flex items-center gap-2">
                            <Filter className="w-5 h-5 text-blue-500" />
                            <Select
                                value={selectedCompany}
                                onValueChange={newCompanyId => {
                                    setSelectedCompany(newCompanyId);
                                    const minDate = getMinStartDate(newCompanyId);
                                    const maxDate = getMaxEndDate(newCompanyId);
                                    setStartDate(startOfMonth(minDate));
                                    setEndDate(maxDate);
                                }}
                            >
                                <SelectTrigger className="w-[200px]">
                                    <SelectValue placeholder={t('logs.selectCompany')} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="1000046">{t('logs.companies.1000046')}</SelectItem>
                                    <SelectItem value="1000047">{t('logs.companies.1000047')}</SelectItem>
                                    <SelectItem value="1000048">{t('logs.companies.1000048')}</SelectItem>
                                    <SelectItem value="1000049">{t('logs.companies.1000049')}</SelectItem>
                                    <SelectItem value="1000061">{t('logs.companies.1000061')}</SelectItem>
                                    <SelectItem value="1000066">{t('logs.companies.1000066')}</SelectItem>
                                    <SelectItem value="1000067">{t('logs.companies.1000067')}</SelectItem>
                                    <SelectItem value="1000068">{t('logs.companies.1000068')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex items-center gap-y-3 flex-wrap">
                            <div className="flex gap-2">
                                <button
                                    onClick={selectCurrentMonth}
                                    className="px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                                >
                                    {t('logs.filters.thisMonth')}
                                </button>
                                <button
                                    onClick={selectLastMonth}
                                    className="px-3 py-1.5 text-xs font-medium bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors"
                                >
                                    {t('logs.filters.lastMonth')}
                                </button>
                                <button
                                    onClick={selectLastThreeMonths}
                                    className="px-3 py-1.5 text-xs font-medium bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                                >
                                    {t('logs.filters.lastThreeMonths')}
                                </button>
                                <div className="h-8 w-px bg-gray-300 mx-3" />
                            </div>

                            <div className="flex items-center gap-3">
                                <CalendarDays className="w-5 h-5 text-gray-500" />
                                <div className="flex items-center gap-2">
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="px-3 py-2 text-sm justify-start text-left font-normal"
                                            >
                                                <Calendar className="mr-2 h-4 w-4" />
                                                {startDate
                                                    ? format(startDate, 'yyyy.MM.dd')
                                                    : t('logs.filters.startDate')}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <CalendarComponent
                                                mode="single"
                                                selected={startDate}
                                                onSelect={date => date && setStartDate(date)}
                                                disabled={date => date < getMinStartDate(selectedCompany)}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>

                                    <span className="text-gray-400">~</span>

                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="px-3 py-2 text-sm justify-start text-left font-normal"
                                            >
                                                <Calendar className="mr-2 h-4 w-4" />
                                                {endDate ? format(endDate, 'yyyy.MM.dd') : t('logs.filters.endDate')}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <CalendarComponent
                                                mode="single"
                                                selected={endDate}
                                                onSelect={date => date && setEndDate(date)}
                                                disabled={date => {
                                                    const maxDate = getMaxEndDate(selectedCompany);
                                                    return (
                                                        date < getMinStartDate(selectedCompany) ||
                                                        date < startDate ||
                                                        date > maxDate
                                                    );
                                                }}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button onClick={handleRefresh} size="sm" variant="outline">
                            <RefreshCw className={`w-4 h-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
                            {t('logs.filters.refresh')}
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
